/**
 * frontend/src/api/organizerRbac.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Client for the organizer team + activity API.
 *
 * Every call goes through `dashboardApi` — the configured axios instance
 * exported from ./index.js. Bare `axios` carries neither the Bearer token nor
 * the active-country header, and its 401s never reach the silent-refresh
 * interceptor, which is exactly the failure `__tests__/noBareAxiosInActions.js`
 * exists to prevent. There is no reason to build a second client here.
 */
import { dashboardApi as API } from "./index.js";

const base = (organizerId) => `/api/ticketing/organizers/${organizerId}`;

// ── catalogue + self ────────────────────────────────────────────────────────
export const apiOrganizerRbacCatalogue = (organizerId) =>
  API.get(`${base(organizerId)}/team/catalogue`);

export const apiOrganizerMyAccess = (organizerId) =>
  API.get(`${base(organizerId)}/team/me`);

// ── members ─────────────────────────────────────────────────────────────────
export const apiOrganizerListMembers = (organizerId) =>
  API.get(`${base(organizerId)}/team`);

/** body: { userId | email | phoneNumber, name?, password?, roleKey, customPermissions? } */
export const apiOrganizerAddMember = (organizerId, body) =>
  API.post(`${base(organizerId)}/team`, body);

export const apiOrganizerChangeMemberRole = (organizerId, userId, roleKey) =>
  API.patch(`${base(organizerId)}/team/${userId}/role`, { roleKey });

/** overrides: [{ key, type: "GRANT" | "DENY", reason?, expiresAt? }] */
export const apiOrganizerSetMemberOverrides = (organizerId, userId, customPermissions) =>
  API.put(`${base(organizerId)}/team/${userId}/permissions`, { customPermissions });

export const apiOrganizerSetMemberStatus = (organizerId, userId, status) =>
  API.patch(`${base(organizerId)}/team/${userId}/status`, { status });

export const apiOrganizerRemoveMember = (organizerId, userId) =>
  API.delete(`${base(organizerId)}/team/${userId}`);

// ── roles ───────────────────────────────────────────────────────────────────
export const apiOrganizerListRoles = (organizerId) =>
  API.get(`${base(organizerId)}/team/roles`);

export const apiOrganizerCreateRole = (organizerId, body) =>
  API.post(`${base(organizerId)}/team/roles`, body);

export const apiOrganizerUpdateRole = (organizerId, roleKey, body) =>
  API.put(`${base(organizerId)}/team/roles/${roleKey}`, body);

export const apiOrganizerDeleteRole = (organizerId, roleKey) =>
  API.delete(`${base(organizerId)}/team/roles/${roleKey}`);

// ── activity ────────────────────────────────────────────────────────────────
/** params: { actorUserId, action, resourceType, resourceId, outcome, from, to, page, limit } */
export const apiOrganizerActivity = (organizerId, params) =>
  API.get(`${base(organizerId)}/activity`, { params });

export const apiOrganizerActivityActors = (organizerId) =>
  API.get(`${base(organizerId)}/activity/actors`);
